import { CopyId } from "@/components/copy-id";
import { ENFORCEMENT_ACTIONS, EnforcementDialog } from "@/components/enforcement-dialog";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useCanWriteHere } from "@/contexts/area.context";
import { cn } from "@/lib/utils";
import { queryKeys } from "@/lib/queryKeys";
import {
  type Enforcement,
  type ReportStatus,
  type ReportWithUsers,
  type UserEnforcementSummary,
  safetyProvider,
} from "@/providers/safety.provider";
import {
  IconChevronLeft,
  IconChevronRight,
  IconGavel,
  IconShield,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

const CATEGORY_LABELS: Record<string, string> = {
  harassment: "Harassment",
  explicit_content: "Explicit content",
  dangerous_behavior: "Dangerous behavior",
  illegal_activity: "Illegal activity",
  hate_speech: "Hate speech",
  spam: "Spam",
  fake_event: "Fake event",
  other: "Other",
};

const ACTION_LABELS: Record<string, string> = Object.fromEntries(
  ENFORCEMENT_ACTIONS.map((a) => [a.value, a.label])
);

// userId omitted → the dialog asks the admin to search for one.
type EnforcementTarget = { userId?: string; username?: string; reportId?: string };

function Pagination({
  page,
  hasNext,
  onChange,
}: {
  page: number;
  hasNext: boolean;
  onChange: (page: number) => void;
}) {
  if (page === 1 && !hasNext) return null;
  return (
    <div className="flex items-center justify-between">
      <Button
        variant="outline"
        size="sm"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="gap-1"
      >
        <IconChevronLeft className="size-3.5" />
        Previous
      </Button>
      <span className="text-sm text-muted-foreground">Page {page}</span>
      <Button
        variant="outline"
        size="sm"
        disabled={!hasNext}
        onClick={() => onChange(page + 1)}
        className="gap-1"
      >
        Next
        <IconChevronRight className="size-3.5" />
      </Button>
    </div>
  );
}

function ReportReviewDialog({
  report,
  open,
  onOpenChange,
  onReviewed,
}: {
  report: ReportWithUsers;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewed: (updated: ReportWithUsers) => void;
}) {
  const [notes, setNotes] = useState(report.admin_notes ?? "");

  const update = useMutation({
    mutationFn: () =>
      safetyProvider.updateReport(report.report_id, {
        status: "reviewed",
        admin_notes: notes || undefined,
      }),
    onSuccess: (updated) => {
      onReviewed({ ...report, ...updated });
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast.error(error.message || "Could not mark the report reviewed."),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mark report reviewed</DialogTitle>
          <DialogDescription>
            {report.reported_username} · {CATEGORY_LABELS[report.category] ?? report.category}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Admin notes (optional)"
          rows={3}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button disabled={update.isPending} onClick={() => update.mutate()}>
            {update.isPending ? "Saving…" : "Mark reviewed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDetailDialog({
  report,
  open,
  onOpenChange,
  onReviewed,
  onEnforce,
  canWrite,
}: {
  report: ReportWithUsers;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewed: (updated: ReportWithUsers) => void;
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const [reviewOpen, setReviewOpen] = useState(false);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Report
              <Badge variant={report.status === "pending" ? "destructive" : "secondary"}>
                {report.status}
              </Badge>
              <Badge variant="outline">
                {CATEGORY_LABELS[report.category] ?? report.category}
              </Badge>
              {report.jev_flagged !== null && (
                <Badge variant="secondary">
                  jev: {Math.round((report.jev_confidence ?? 0) * 100)}%
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {new Date(report.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Report ID
              </span>
              <CopyId id={report.report_id} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Reported user
                </span>
                <span className="font-medium wrap-break-word">{report.reported_username}</span>
                <span className="text-xs text-muted-foreground wrap-break-word">
                  {report.reported_email}
                </span>
                <CopyId id={report.reported_user_id} />
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Reporter
                </span>
                {report.reporter_id ? (
                  <>
                    <span className="font-medium wrap-break-word">
                      {report.reporter_username ?? "—"}
                    </span>
                    <span className="text-xs text-muted-foreground wrap-break-word">
                      {report.reporter_email ?? "—"}
                    </span>
                    <CopyId id={report.reporter_id} />
                  </>
                ) : (
                  <Badge variant="secondary" className="w-fit">
                    Auto
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Content
              </span>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded w-fit">
                {report.content_type} · {report.content_id}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Slug</span>
              <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded w-fit break-all">
                {report.report_slug}
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Description
              </span>
              <p className="whitespace-pre-wrap">{report.description || "—"}</p>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Admin notes
              </span>
              <p className="whitespace-pre-wrap text-muted-foreground">
                {report.admin_notes || "—"}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {canWrite && (
              <Button
                variant="outline"
                onClick={() =>
                  onEnforce({
                    userId: report.reported_user_id,
                    username: report.reported_username,
                    reportId: report.report_id,
                  })
                }
              >
                <IconGavel className="size-3.5 mr-1" />
                Enforce
              </Button>
            )}
            {report.status === "pending" && canWrite && (
              <Button onClick={() => setReviewOpen(true)}>
                <IconShield className="size-3.5 mr-1" />
                Mark reviewed
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reviewOpen && (
        <ReportReviewDialog
          report={report}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onReviewed={(updated) => {
            onReviewed(updated);
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
}

function ReportTableRow({
  report,
  onReviewed,
  onEnforce,
  canWrite,
}: {
  report: ReportWithUsers;
  onReviewed: (updated: ReportWithUsers) => void;
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setDetailOpen(true)}
      >
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">
          {new Date(report.created_at).toLocaleDateString()}
        </TableCell>
        <TableCell className="align-top">
          <Badge variant={report.status === "pending" ? "destructive" : "secondary"}>
            {report.status}
          </Badge>
        </TableCell>
        <TableCell className="align-top">
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant="outline">
              {CATEGORY_LABELS[report.category] ?? report.category}
            </Badge>
            {report.jev_flagged !== null && (
              <Badge variant="secondary" className="whitespace-nowrap">
                jev: {Math.round((report.jev_confidence ?? 0) * 100)}%
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="min-w-0 align-top">
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium truncate">{report.reported_username}</span>
            <span className="text-xs text-muted-foreground truncate">
              {report.reported_email}
            </span>
          </div>
        </TableCell>
        <TableCell className="min-w-0 align-top">
          {report.reporter_id ? (
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="truncate">{report.reporter_username ?? "—"}</span>
              <span className="text-xs text-muted-foreground truncate">
                {report.reporter_email ?? "—"}
              </span>
            </div>
          ) : (
            <Badge variant="secondary">Auto</Badge>
          )}
        </TableCell>
        <TableCell className="max-w-[220px] align-top">
          <span
            className="block truncate font-mono text-xs bg-muted px-2 py-0.5 rounded w-fit max-w-full"
            title={report.report_slug}
          >
            {report.report_slug}
          </span>
        </TableCell>
        <TableCell className="max-w-[220px] align-top">
          {report.description ? (
            <p className="truncate text-muted-foreground">{report.description}</p>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
          {report.admin_notes && (
            <p className="text-xs italic text-muted-foreground mt-1 truncate">
              Notes: {report.admin_notes}
            </p>
          )}
        </TableCell>
        <TableCell className="text-right align-top">
          <div
            className="flex justify-end gap-1.5 flex-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            {report.status === "pending" && canWrite && (
              <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)}>
                <IconShield className="size-3.5 mr-1" />
                Review
              </Button>
            )}
            {canWrite && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onEnforce({
                    userId: report.reported_user_id,
                    username: report.reported_username,
                    reportId: report.report_id,
                  })
                }
              >
                <IconGavel className="size-3.5 mr-1" />
                Enforce
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {reviewOpen && (
        <ReportReviewDialog
          report={report}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onReviewed={onReviewed}
        />
      )}
      {detailOpen && (
        <ReportDetailDialog
          report={report}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          onReviewed={onReviewed}
          onEnforce={onEnforce}
          canWrite={canWrite}
        />
      )}
    </>
  );
}

function enforcementState(e: Enforcement): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  if (e.lifted_at) return { label: "lifted", variant: "secondary" };
  // expires_at only locks the account for a ban — it's a no-op on strikes/warnings.
  if (e.action === "ban_user" && e.expires_at && new Date(e.expires_at) <= new Date())
    return { label: "expired", variant: "secondary" };
  if (e.action === "issue_warning") return { label: "warning", variant: "outline" };
  if (e.action === "strike_user") return { label: "strike", variant: "outline" };
  return { label: "active", variant: "destructive" };
}

function LiftEnforcementDialog({
  enforcement,
  open,
  onOpenChange,
}: {
  enforcement: Enforcement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");

  const lift = useMutation({
    mutationFn: () => safetyProvider.liftEnforcement(enforcement.enforcement_id, reason.trim()),
    onSuccess: () => {
      toast.success("Enforcement lifted.");
      queryClient.invalidateQueries({ queryKey: ["enforcements"] });
      queryClient.invalidateQueries({
        queryKey: queryKeys.userEnforcements(enforcement.user_id),
      });
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast.error(error.message || "Could not lift the enforcement."),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Lift this {(ACTION_LABELS[enforcement.action] ?? enforcement.action).toLowerCase()}?
          </DialogTitle>
          <DialogDescription>
            They may regain access immediately if this was an active ban. The
            original reason stays on the record — this note goes to the audit log
            only. The user's app may still show them as suspended until it next
            checks in; it clears itself without any action from them.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being lifted? (goes to the audit log)"
          rows={3}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={lift.isPending}>
            Cancel
          </Button>
          <Button disabled={lift.isPending || !reason.trim()} onClick={() => lift.mutate()}>
            {lift.isPending ? "Lifting…" : "Confirm lift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnforcementDetailDialog({
  enforcement,
  open,
  onOpenChange,
  canWrite,
}: {
  enforcement: Enforcement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canWrite: boolean;
}) {
  const [liftOpen, setLiftOpen] = useState(false);
  const state = enforcementState(enforcement);
  const canLift = !enforcement.lifted_at && canWrite;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Enforcement
              <Badge variant={state.variant}>{state.label}</Badge>
              <Badge variant="outline">
                {ACTION_LABELS[enforcement.action] ?? enforcement.action}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {new Date(enforcement.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Enforcement ID
              </span>
              <CopyId id={enforcement.enforcement_id} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">User</span>
              <CopyId id={enforcement.user_id} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Issued by
              </span>
              <CopyId id={enforcement.admin_user_id} />
            </div>
            {enforcement.report_id && (
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">
                  Linked report
                </span>
                <CopyId id={enforcement.report_id} />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Reason
              </span>
              <p className="whitespace-pre-wrap">{enforcement.reason}</p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">
                Status
              </span>
              <p className="text-muted-foreground">
                {enforcement.action !== "ban_user"
                  ? "Access unaffected"
                  : enforcement.expires_at
                    ? `Expires ${new Date(enforcement.expires_at).toLocaleString()}`
                    : "Permanent"}
              </p>
              {enforcement.lifted_at && (
                <p className="text-muted-foreground">
                  Lifted {new Date(enforcement.lifted_at).toLocaleString()}
                  {enforcement.lifted_by && ` by ${enforcement.lifted_by}`}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {canLift && (
              <Button variant="destructive" onClick={() => setLiftOpen(true)}>
                Lift
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {liftOpen && (
        <LiftEnforcementDialog
          enforcement={enforcement}
          open={liftOpen}
          onOpenChange={(next) => {
            setLiftOpen(next);
            if (!next) onOpenChange(false);
          }}
        />
      )}
    </>
  );
}

function EnforcementTableRow({
  enforcement,
  canWrite,
}: {
  enforcement: Enforcement;
  canWrite: boolean;
}) {
  const [liftOpen, setLiftOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const state = enforcementState(enforcement);
  const canLift = !enforcement.lifted_at && canWrite;

  return (
    <>
      <TableRow
        className={cn(
          "cursor-pointer hover:bg-muted/50",
          enforcement.lifted_at && "opacity-60"
        )}
        onClick={() => setDetailOpen(true)}
      >
        <TableCell className="align-top">
          <Badge variant={state.variant}>{state.label}</Badge>
        </TableCell>
        <TableCell className="align-top">
          <Badge variant="outline">
            {ACTION_LABELS[enforcement.action] ?? enforcement.action}
          </Badge>
        </TableCell>
        <TableCell className="min-w-0 align-top">
          <CopyId id={enforcement.user_id} />
        </TableCell>
        <TableCell className="max-w-[240px] align-top">
          <p className="truncate text-muted-foreground">{enforcement.reason}</p>
          {enforcement.report_id && (
            <span className="text-xs text-muted-foreground">Linked to a report</span>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">
          {new Date(enforcement.created_at).toLocaleDateString()}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground whitespace-nowrap align-top">
          {enforcement.lifted_at
            ? `Lifted ${new Date(enforcement.lifted_at).toLocaleDateString()}`
            : enforcement.action !== "ban_user"
              ? "Access unaffected"
              : enforcement.expires_at
                ? `Expires ${new Date(enforcement.expires_at).toLocaleDateString()}`
                : "Permanent"}
        </TableCell>
        <TableCell className="text-right align-top">
          {canLift && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setLiftOpen(true);
              }}
            >
              Lift
            </Button>
          )}
        </TableCell>
      </TableRow>

      {liftOpen && (
        <LiftEnforcementDialog
          enforcement={enforcement}
          open={liftOpen}
          onOpenChange={setLiftOpen}
        />
      )}
      {detailOpen && (
        <EnforcementDetailDialog
          enforcement={enforcement}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          canWrite={canWrite}
        />
      )}
    </>
  );
}

const PERMANENT_SENTINEL_YEAR = 9999;

function isPermanentSuspension(suspendedUntil: string) {
  return new Date(suspendedUntil).getUTCFullYear() >= PERMANENT_SENTINEL_YEAR;
}

function EnforcementChips({ user }: { user: UserEnforcementSummary }) {
  const chips: { key: string; label: string; variant: "destructive" | "secondary" | "outline" }[] = [];

  if (user.ban_count > 0)
    chips.push({ key: "ban", label: `${user.ban_count} ban${user.ban_count > 1 ? "s" : ""}`, variant: "destructive" });
  if (user.strike_count > 0)
    chips.push({ key: "strike", label: `${user.strike_count} strike${user.strike_count > 1 ? "s" : ""}`, variant: "secondary" });
  if (user.warning_count > 0)
    chips.push({ key: "warning", label: `${user.warning_count} warning${user.warning_count > 1 ? "s" : ""}`, variant: "outline" });

  if (!chips.length)
    return <span className="text-xs text-muted-foreground">Clean record</span>;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {chips.map((c) => (
        <Badge key={c.key} variant={c.variant}>
          {c.label}
        </Badge>
      ))}
      {user.active_enforcement_count > 0 && (
        <Badge variant="destructive">{user.active_enforcement_count} active</Badge>
      )}
    </div>
  );
}

function UserDetailDialog({
  user,
  open,
  onOpenChange,
  onEnforce,
  canWrite,
}: {
  user: UserEnforcementSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.userEnforcements(user.user_id),
    queryFn: () => safetyProvider.listUserEnforcements(user.user_id, { limit: 50 }),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {user.username}
            {user.is_deleted && (
              <Badge variant="secondary" className="text-[10px]">
                deleted
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              User ID
            </span>
            <CopyId id={user.user_id} />
          </div>

          <EnforcementChips user={user} />

          {user.suspended_until && (
            <p className="text-destructive">
              {isPermanentSuspension(user.suspended_until)
                ? "Permanently suspended"
                : `Suspended until ${new Date(user.suspended_until).toLocaleString()}`}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground uppercase tracking-wide">
              Enforcement history
            </span>
            {isLoading ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-md" />
                ))}
              </div>
            ) : isError ? (
              <p className="text-sm text-muted-foreground">Failed to load history.</p>
            ) : !data?.data.length ? (
              <p className="text-sm text-muted-foreground">No enforcements on record.</p>
            ) : (
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>State</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Expiry / lifted</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((e) => (
                      <EnforcementTableRow key={e.enforcement_id} enforcement={e} canWrite={canWrite} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canWrite && (
            <Button onClick={() => onEnforce({ userId: user.user_id, username: user.username })}>
              <IconGavel className="size-3.5 mr-1" />
              Issue enforcement
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserEnforcementRow({
  user,
  onEnforce,
  canWrite,
}: {
  user: UserEnforcementSummary;
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailOpen(true)}>
        <TableCell>
          <CopyId id={user.user_id} />
        </TableCell>
        <TableCell>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium flex items-center gap-1.5">
              {user.username}
              {user.is_deleted && (
                <Badge variant="secondary" className="text-[10px]">
                  deleted
                </Badge>
              )}
            </span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </TableCell>
        <TableCell>
          <EnforcementChips user={user} />
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {user.last_action ? (
            <div className="flex flex-col gap-0.5">
              <span>{ACTION_LABELS[user.last_action] ?? user.last_action}</span>
              {user.last_enforcement_at && (
                <span>{new Date(user.last_enforcement_at).toLocaleDateString()}</span>
              )}
            </div>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell className="text-xs">
          {user.suspended_until ? (
            <span className="text-destructive">
              {isPermanentSuspension(user.suspended_until)
                ? "Permanent"
                : `Until ${new Date(user.suspended_until).toLocaleDateString()}`}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEnforce({ userId: user.user_id, username: user.username });
              }}
            >
              <IconGavel className="size-3.5 mr-1" />
              Enforce
            </Button>
          )}
        </TableCell>
      </TableRow>

      {detailOpen && (
        <UserDetailDialog
          user={user}
          open={detailOpen}
          onOpenChange={setDetailOpen}
          onEnforce={onEnforce}
          canWrite={canWrite}
        />
      )}
    </>
  );
}

function UsersTab({
  onEnforce,
  canWrite,
}: {
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const [enforcedOnly, setEnforcedOnly] = useState(true);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.userEnforcementSummaries(enforcedOnly, page),
    queryFn: () =>
      safetyProvider.listUserEnforcementSummaries({
        enforced_only: enforcedOnly,
        page,
        limit: 50,
      }),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5">
        {([true, false] as const).map((v) => (
          <Button
            key={String(v)}
            size="sm"
            variant={enforcedOnly === v ? "default" : "outline"}
            onClick={() => {
              setEnforcedOnly(v);
              setPage(1);
            }}
          >
            {v ? "Enforced only" : "All users"}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Failed to load users.</p>
      ) : !data?.data.length ? (
        <p className="text-sm text-muted-foreground">No users found.</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Enforcements</TableHead>
                <TableHead>Last action</TableHead>
                <TableHead>Suspended</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((user) => (
                <UserEnforcementRow
                  key={user.user_id}
                  user={user}
                  onEnforce={onEnforce}
                  canWrite={canWrite}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Pagination page={page} hasNext={!!data?.has_next} onChange={setPage} />
    </div>
  );
}

function ReportsTab({
  onEnforce,
  canWrite,
}: {
  onEnforce: (target: EnforcementTarget) => void;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ReportStatus | "">("");
  const [page, setPage] = useState(1);
  const queryKey = queryKeys.safetyReportsDetailed(statusFilter, page);

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      safetyProvider.listReportsDetailed({
        status: statusFilter || undefined,
        page,
        limit: 50,
      }),
  });

  function handleReviewed(updated: ReportWithUsers) {
    queryClient.setQueryData(queryKey, (prev: typeof data) =>
      prev
        ? {
            ...prev,
            data: prev.data.map((r) =>
              r.report_id === updated.report_id ? updated : r
            ),
          }
        : prev
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-1.5">
        {(["", "pending", "reviewed"] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? "default" : "outline"}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
          >
            {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Failed to load reports.</p>
      ) : !data?.data.length ? (
        <p className="text-sm text-muted-foreground">
          {statusFilter === "pending" || statusFilter === ""
            ? "No pending reports — queue is clear."
            : "No reports found."}
        </p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Reported</TableHead>
                <TableHead>Reporter</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Description</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((report) => (
                <ReportTableRow
                  key={report.report_id}
                  report={report}
                  onReviewed={handleReviewed}
                  onEnforce={onEnforce}
                  canWrite={canWrite}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Pagination page={page} hasNext={!!data?.has_next} onChange={setPage} />
    </div>
  );
}

function EnforcementsTab({
  onNew,
  canWrite,
}: {
  onNew: () => void;
  canWrite: boolean;
}) {
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.enforcements(activeOnly, page),
    queryFn: () => safetyProvider.listEnforcements({ active: activeOnly, page, limit: 50 }),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5">
          {([true, false] as const).map((v) => (
            <Button
              key={String(v)}
              size="sm"
              variant={activeOnly === v ? "default" : "outline"}
              onClick={() => {
                setActiveOnly(v);
                setPage(1);
              }}
            >
              {v ? "Active" : "All"}
            </Button>
          ))}
        </div>
        {canWrite && (
          <Button size="sm" onClick={onNew}>
            <IconGavel className="size-3.5 mr-1" />
            New enforcement
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Failed to load enforcements.</p>
      ) : !data?.data.length ? (
        <p className="text-sm text-muted-foreground">
          {activeOnly ? "No enforcements are currently in force." : "No enforcements found."}
        </p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expiry / lifted</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.data.map((enforcement) => (
                <EnforcementTableRow
                  key={enforcement.enforcement_id}
                  enforcement={enforcement}
                  canWrite={canWrite}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Pagination page={page} hasNext={!!data?.has_next} onChange={setPage} />
    </div>
  );
}

export function UserSafetyPage() {
  const [tab, setTab] = useState("reports");
  const [target, setTarget] = useState<EnforcementTarget | null>(null);
  const canWrite = useCanWriteHere();

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="User Safety" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6 overflow-y-auto">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="enforcements">Enforcements</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="reports" className="mt-4">
            <ReportsTab onEnforce={setTarget} canWrite={canWrite} />
          </TabsContent>

          <TabsContent value="enforcements" className="mt-4">
            <EnforcementsTab onNew={() => setTarget({})} canWrite={canWrite} />
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <UsersTab onEnforce={setTarget} canWrite={canWrite} />
          </TabsContent>
        </Tabs>
      </div>

      {target && (
        <EnforcementDialog
          open
          onOpenChange={(open) => !open && setTarget(null)}
          userId={target.userId}
          username={target.username}
          reportId={target.reportId}
        />
      )}
    </div>
  );
}
